// Tiny dependency-free tooltip: hover/focus on pointer devices, tap-to-toggle on
// touch. Used for the checked-off-by avatar so the name no longer needs its own
// column of horizontal space (D-7). No tooltip library (see EDITOR_ROW_ACTIONS).

import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";

export interface HoverTapTipProps {
  /** Text shown in the bubble and used as the trigger's accessible label. */
  label: string;
  children: ReactNode;
  className?: string;
}

export function HoverTapTip({ label, children, className = "" }: HoverTapTipProps) {
  const [open, setOpen] = useState(false);
  const tipId = useId();
  const wrapRef = useRef<HTMLSpanElement>(null);

  // Dismiss the tap-opened bubble on any outside pointer / Escape.
  useEffect(() => {
    if (!open) return;
    function onDocPointer(e: PointerEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onDocPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDocPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span
      ref={wrapRef}
      className={`relative inline-flex shrink-0 ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={label}
        aria-describedby={open ? tipId : undefined}
        className="inline-flex rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => {
          // Keep the row / checkbox from reacting to the tap.
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        {children}
      </button>
      {open ? (
        <span
          id={tipId}
          role="tooltip"
          className="pointer-events-none absolute right-0 top-full z-30 mt-1 max-w-[60vw] truncate rounded-lg bg-ink px-2 py-1 text-xs font-medium text-white shadow-lg"
        >
          {label}
        </span>
      ) : null}
    </span>
  );
}
